import { useEffect, useRef, useState } from 'react';
import ChatMessage from './ChatMessage';
import MarkdownBlock from '../LazyMarkdownBlock';
import KnowledgeReferences from './KnowledgeReferences';
import { getRoomWorkflowTone } from '../../lib/roomWorkflow';

export default function SinkChatPane({
  sinkMessages,
  sinkMessagesEndRef,
  sinkScrollContainerRef,
  sinkRoomScrollPositions,
  sinkRoomId,
  scrollToBottom,
  highlightedRawMessageIds,
  concepts,
  notes,
  insights,
  getAssetTypeLabel,
  onReferenceClick,
  openMemoModal,
  isSummarizing,
  isSaving,
  sinkInputText,
  setSinkInputText,
  inputMode,
  setInputMode,
  setHistoryIndex,
  handleInputKeyDown,
  handleSave,
  isSinkRoomNearLimit,
  isSinkRoomAtLimit,
  MAX_MESSAGES_PER_ROOM,
  INPUT_MODE_DETAILS,
  handleStartNewRoom,
  handleSummarizeAndNewRoom,
  setIsChatListModalOpen,
  debugMode,
  handleResend,
  sinkContextTitle,
  isDeepDiscussMode,
  setIsDeepDiscussMode,
  isMultilineMode,
  setIsMultilineMode,
  openSettings,
  activeReferencedAssets = [],
  onRemoveAsset,
  isLargeScreen = false,
  sinkDraftMetadata = null,
  onLongPasteDetected,
  longPasteCharThreshold = 1000,
  sinkInputCharLimit = 50000,
  aiActiveInputCharLimit = 4000,
  currentRoomWorkflow = null,
  showFirstUseFlow = false,
  onOpenSummaryNote,
}) {
  const textareaRef = useRef(null);
  const inputRef = useRef(null);
  const isModeInitRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showDeepTooltip, setShowDeepTooltip] = useState(false);
  const isMobileInput = !isLargeScreen;
  const useTextareaInput = isMobileInput || isMultilineMode;
  const trimmedInputLength = sinkInputText.trim().length;
  const isAiActiveMode = inputMode === 'discuss' || inputMode === 'anti';
  const isOverSinkInputLimit = trimmedInputLength > sinkInputCharLimit;
  const isOverAiActiveInputLimit = isAiActiveMode && trimmedInputLength > aiActiveInputCharLimit;
  const isInputOverLimit = isOverSinkInputLimit || isOverAiActiveInputLimit;

  useEffect(() => {
    const container = sinkScrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Save scroll position
      if (sinkRoomScrollPositions) {
        sinkRoomScrollPositions.current[sinkRoomId] = scrollTop;
      }

      // Show/hide jump button
      const isNearBottom = scrollHeight - scrollTop <= clientHeight + 300;
      setShowScrollToBottom(!isNearBottom && scrollHeight > clientHeight);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [sinkScrollContainerRef, sinkRoomScrollPositions, sinkRoomId]);

  // iOS scrolls the document when a new input element is mounted on mode switch.
  // Reset window scroll and re-focus without scroll to keep the keyboard stable.
  useEffect(() => {
    if (isModeInitRef.current) { isModeInitRef.current = false; return; }
    window.scrollTo(0, 0);
    const el = useTextareaInput ? textareaRef.current : inputRef.current;
    if (el) el.focus({ preventScroll: true });
  }, [useTextareaInput]);

  // Auto-resize textarea when text changes (especially when cleared)
  useEffect(() => {
    if (useTextareaInput && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = isLargeScreen ? 140 : 160;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [sinkInputText, useTextareaInput, isLargeScreen]);

  const handleSinkInputKeyDown = (e) => {
    if (isMobileInput && e.key === 'Enter') return;
    handleInputKeyDown(e, 'sink');
  };

  const handleSinkPaste = (e) => {
    const pastedText = e.clipboardData?.getData('text') || '';
    if (pastedText.length >= longPasteCharThreshold) {
      onLongPasteDetected?.({ charCount: pastedText.length });
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      {/* Header */}
      <div className="flex items-center justify-between px-3 border-b border-gray-800 bg-gray-900/50 shrink-0 h-12">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsChatListModalOpen(true)}
            className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors shrink-0"
            title="ルーム一覧"
            aria-label="ルーム一覧"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => handleStartNewRoom()}
            className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-blue-400 transition-colors shrink-0"
            title="新規ルーム"
            aria-label="新規ルーム"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        <div className="flex bg-gray-950 rounded-full p-0.5 border border-gray-800 shrink-0">
          <button onClick={() => setInputMode('input')} className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'input' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Sink</button>
          <button onClick={() => setInputMode('discuss')} className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'discuss' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Discuss</button>
          <button onClick={() => setInputMode('anti')} className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all active:scale-95 whitespace-nowrap ${inputMode === 'anti' ? 'bg-rose-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}>Anti</button>
        </div>
      </div>

      <div className="px-4 pt-3 pb-0 shrink-0 space-y-2">
        <div className={`rounded-lg border px-3 py-2 text-[11px] ${INPUT_MODE_DETAILS[inputMode].accent}`}>
          <div className="flex items-baseline gap-2">
            <p className="font-bold text-white whitespace-nowrap">{INPUT_MODE_DETAILS[inputMode].title}</p>
            <p className="leading-relaxed opacity-90">{INPUT_MODE_DETAILS[inputMode].description}</p>
          </div>
        </div>
        {sinkContextTitle && (
          <div className="flex items-center gap-2 rounded-lg bg-indigo-950/50 border border-indigo-700/40 px-3 py-2">
            <svg className="w-3.5 h-3.5 text-indigo-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
            <span className="text-xs font-bold text-indigo-300 truncate">{sinkContextTitle}</span>
          </div>
        )}
        {showFirstUseFlow && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/20 px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300">最初の流れ</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {['1. SinkにRaw保存', '2. Noteまとめ', '3. Hint確認', '4. 必要ならAsset化'].map((step) => (
                <span key={step} className="rounded-full border border-emerald-700/40 bg-emerald-950/40 px-2 py-1 text-[10px] font-bold text-emerald-100">
                  {step}
                </span>
              ))}
            </div>
          </div>
        )}
        {currentRoomWorkflow && (
          <div className={`rounded-lg border px-3 py-2 text-[11px] ${getRoomWorkflowTone(currentRoomWorkflow.key).panelClass}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[10px] font-black tracking-wide text-white/90">
                    {currentRoomWorkflow.label}
                  </span>
                </div>
                <p className="mt-1 leading-relaxed opacity-90">{currentRoomWorkflow.description}</p>
              </div>
              {currentRoomWorkflow.action === 'memoize' ? (
                <button
                  type="button"
                  onClick={() => openMemoModal()}
                  disabled={isSummarizing}
                  className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-[10px] font-bold text-white transition-all hover:bg-white/15 disabled:opacity-50"
                >
                  {currentRoomWorkflow.ctaLabel}
                </button>
              ) : currentRoomWorkflow.action === 'open_note' ? (
                <button
                  type="button"
                  onClick={() => onOpenSummaryNote?.()}
                  className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-[10px] font-bold text-white transition-all hover:bg-white/15"
                >
                  {currentRoomWorkflow.ctaLabel}
                </button>
              ) : null}
            </div>
            <p className="mt-2 text-[10px] leading-relaxed opacity-75">{currentRoomWorkflow.timingHint}</p>
          </div>
        )}
      </div>

      <div ref={sinkScrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
        {sinkMessages.length === 0 && (
          <div className="text-center text-xs text-gray-500 pt-8">
            まだメッセージがありません。
          </div>
        )}
        {sinkMessages.map((message) => {
          const isHighlightedRaw = highlightedRawMessageIds.includes(message.id);
          const source = message.source ?? message.role;
          const isExternalClip = message.metadata?.inputKind === 'paste' || message.metadata?.captureType === 'external_clip';

          if (message.kind === 'error' || source === 'assistant' || source === 'buddy') {
             return (
               <ChatMessage
                 key={message.id}
                 message={message}
                 concepts={concepts}
                 notes={notes}
                 insights={insights}
                 debugMode={debugMode}
                 handleResend={handleResend}
                 onReferenceClick={onReferenceClick}
                 getAssetTypeLabel={getAssetTypeLabel}
                 openMemoModal={openMemoModal}
                 isSummarizing={isSummarizing}
                 openSettings={openSettings}
               />
             );
          }

          return (
            <div key={message.id} className="flex justify-end">
              <div className={`bg-blue-600 text-white rounded-br-sm ${isHighlightedRaw ? 'ring-1 ring-amber-500/50 bg-amber-900/10' : ''} p-3 rounded-2xl max-w-[85%] text-sm shadow-sm transition-colors`}>
                {isExternalClip && (
                  <div className="mb-2 inline-flex items-center gap-1 rounded-full border border-blue-200/30 bg-blue-950/30 px-2 py-0.5 text-[10px] font-bold text-blue-50">
                    <span>外部クリップ</span>
                    {message.metadata?.charCount && <span className="opacity-70">{message.metadata.charCount.toLocaleString()}字</span>}
                  </div>
                )}
                <MarkdownBlock text={message.body ?? message.text} />
                <KnowledgeReferences
                  metadata={message.metadata}
                  concepts={concepts}
                  notes={notes}
                  insights={insights}
                  onReferenceClick={onReferenceClick}
                  className="mt-3 pt-3 border-t border-gray-700/50"
                />
                {message.kind === 'error' && (
                  <div className="mt-2 text-[10px] text-red-300 font-bold uppercase">error</div>
                )}
                {message.kind === 'memo_suggestion' && (
                  <button onClick={() => openMemoModal()} disabled={isSummarizing} className="mt-3 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all active:scale-95 flex items-center gap-1.5">
                    {isSummarizing && (
                      <svg className="animate-spin h-3 w-3 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSummarizing ? 'Noteまとめ中' : 'Noteまとめ'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {isSaving && (
          <div className="flex justify-start">
            <div className="bg-gray-800 border border-gray-700 p-3 rounded-2xl rounded-bl-sm max-w-[85%] text-sm text-gray-400 animate-pulse">
              Thinking...
            </div>
          </div>
        )}
        <div ref={sinkMessagesEndRef} />
      </div>

      {/* Scroll to Bottom Button */}
      {showScrollToBottom && (
        <button
          onClick={() => scrollToBottom?.(sinkMessagesEndRef, sinkScrollContainerRef, { force: true })}
          className="absolute bottom-[160px] right-6 bg-gray-800/90 hover:bg-gray-700 border border-gray-600 text-white p-2.5 rounded-full shadow-2xl transition-all active:scale-90 z-30 animate-in fade-in slide-in-from-bottom-2 duration-200"
          aria-label="最新のメッセージへ移動"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* Input Form */}
      <div className="shrink-0 bg-gray-900 border-t border-gray-800 shadow-2xl">
        <div className={`px-3 py-3`}>
          {activeReferencedAssets.length > 0 && (
            <div className="mb-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1">
              <span className="text-[10px] font-bold text-gray-500 whitespace-nowrap mr-0.5">参照中:</span>
              {activeReferencedAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="flex items-center gap-1 bg-emerald-950/40 text-emerald-300 border border-emerald-800/30 px-2 py-0.5 rounded-full text-[10px] whitespace-nowrap shadow-sm"
                >
                  <span className="truncate max-w-[80px] lg:max-w-[120px]">{asset.title}</span>
                  <button
                    onClick={() => onRemoveAsset?.(asset.id)}
                    className="hover:text-emerald-100 p-0.5 -mr-1 transition-colors"
                    aria-label={`${asset.title}の参照を解除`}
                  >
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {(isSinkRoomNearLimit || isSinkRoomAtLimit) && (
            <div className={`mb-2 p-2 rounded-xl text-[11px] font-bold shadow-lg transition-all border ${isSinkRoomAtLimit ? 'bg-red-950/40 text-red-200 border-red-500/30' : 'bg-amber-950/40 text-amber-200 border-amber-500/30'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className={`flex h-2 w-2 rounded-full ${isSinkRoomAtLimit ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`} />
                  <span>{isSinkRoomAtLimit ? '上限に達しました。Noteまとめして新ルームを開始してください。' : `残り${MAX_MESSAGES_PER_ROOM - sinkMessages.length}通で上限です。`}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="opacity-60 tabular-nums">{sinkMessages.length}{isSinkRoomAtLimit || isSinkRoomNearLimit ? `/${MAX_MESSAGES_PER_ROOM}` : ''}</span>
                  {isSinkRoomAtLimit && (
                    <button
                      type="button"
                      onClick={() => handleSummarizeAndNewRoom()}
                      disabled={isSummarizing}
                      className="ml-1 bg-red-600 hover:bg-red-500 text-white px-2 py-1 rounded-lg text-[10px] transition-all active:scale-95 disabled:opacity-50 flex items-center gap-1"
                    >
                      {isSummarizing && (
                        <svg className="animate-spin h-2.5 w-2.5 text-white" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {isSummarizing ? 'まとめ中...' : 'まとめ&新ルーム'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {sinkDraftMetadata?.inputKind === 'paste' && (
            <div className="mb-2 rounded-lg border border-blue-500/30 bg-blue-950/30 px-3 py-2 text-[11px] text-blue-100">
              <span className="font-bold">外部クリップ:</span> 長文ペーストとして保存します。{sinkDraftMetadata.pastedCharCount?.toLocaleString()}字
            </div>
          )}
          {isOverAiActiveInputLimit && (
            <div className="mb-2 rounded-lg border border-amber-500/30 bg-amber-950/40 px-3 py-2 text-[11px] font-bold text-amber-100">
              AIモードは{aiActiveInputCharLimit.toLocaleString()}字までです。長文はSinkで保存してからNoteまとめしてください。
            </div>
          )}
          {isOverSinkInputLimit && (
            <div className="mb-2 rounded-lg border border-red-500/30 bg-red-950/40 px-3 py-2 text-[11px] font-bold text-red-100">
              入力は{sinkInputCharLimit.toLocaleString()}字までです。
            </div>
          )}
          <form onSubmit={(e) => handleSave(e, inputMode)} className="flex items-center gap-1.5">
            {/* Left action icons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => openMemoModal()}
                disabled={sinkMessages.length === 0 || isSummarizing}
                title="まとめ"
                aria-label="まとめ"
                className={`${isLargeScreen ? 'p-2' : 'p-2.5'} rounded-full flex-shrink-0 shadow-lg transition-all active:scale-95 disabled:opacity-30 disabled:cursor-default ${isSummarizing ? 'bg-purple-500 text-white flex items-center justify-center' : 'bg-purple-700 hover:bg-purple-600 text-white'}`}
              >
                {isSummarizing ? (
                  <svg className={`${isLargeScreen ? 'w-4 h-4' : 'w-5 h-5'} animate-spin`} viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className={`${isLargeScreen ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
              </button>
              <div className="relative">
                <button
                  type="button"
                  disabled={inputMode === 'input'}
                  onClick={() => {
                    if (isDeepDiscussMode) {
                      setIsDeepDiscussMode(false);
                      setShowDeepTooltip(false);
                    } else {
                      setShowDeepTooltip(v => !v);
                    }
                  }}
                  title="深い議論"
                  aria-label="深い議論"
                  className={`${isLargeScreen ? 'p-2' : 'p-2.5'} rounded-full flex-shrink-0 shadow-lg transition-all active:scale-95 disabled:opacity-30 disabled:cursor-default ${
                    isDeepDiscussMode
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  <svg className={`${isLargeScreen ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </button>
                {showDeepTooltip && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowDeepTooltip(false)} />
                    <div className="absolute bottom-full left-0 mb-2 w-56 bg-gray-800 border border-gray-700 rounded-2xl p-3 shadow-2xl z-50">
                      <p className="text-[11px] font-bold text-white mb-1">深い議論モード</p>
                      <p className="text-[11px] text-gray-400 leading-relaxed mb-3">AIの回答が長くなり、問いかけの制限が外れます。じっくり掘り下げたいときに。</p>
                      <button
                        type="button"
                        onClick={() => { setIsDeepDiscussMode(true); setShowDeepTooltip(false); }}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold py-1.5 rounded-lg transition-colors"
                      >
                        有効にする
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="relative flex-1 min-w-0 flex items-center">
              {useTextareaInput ? (
                <textarea
                  ref={textareaRef}
                  rows={isMobileInput ? "1" : "2"}
                  value={sinkInputText}
                  onChange={(e) => { setSinkInputText(e.target.value); setHistoryIndex(null); }}
                  onKeyDown={handleSinkInputKeyDown}
                  onPaste={handleSinkPaste}
                  placeholder={isSinkRoomAtLimit ? "上限のため入力できません" : (isMobileInput ? "思考を入力..." : "思考を入力... (Ctrl+Enterで送信)")}
                  disabled={isSinkRoomAtLimit || isSaving}
                  className={`flex-1 bg-gray-800 border ${isSinkRoomAtLimit ? 'border-red-500/50' : 'border-gray-700'} text-white pl-4 ${isMobileInput ? 'pr-4 min-h-[44px]' : 'pr-10 min-h-[80px]'} py-2.5 rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-all shadow-inner resize-none overflow-y-auto leading-normal no-scrollbar disabled:opacity-50 disabled:cursor-not-allowed`}
                />
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  value={sinkInputText}
                  onChange={(e) => { setSinkInputText(e.target.value); setHistoryIndex(null); }}
                  onKeyDown={handleSinkInputKeyDown}
                  onPaste={handleSinkPaste}
                  placeholder={isSinkRoomAtLimit ? "上限のため入力できません" : "思考を入力... (Enterで送信)"}
                  disabled={isSinkRoomAtLimit || isSaving}
                  className={`w-full min-w-0 bg-gray-800 border ${isSinkRoomAtLimit ? 'border-red-500/50' : 'border-gray-700'} text-white pl-4 pr-10 h-[40px] rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-all shadow-inner disabled:opacity-50 disabled:cursor-not-allowed`}
                />
              )}
              {!isMobileInput && (
                <div className="absolute right-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setIsMultilineMode(!isMultilineMode)}
                    className={`p-1.5 rounded-lg transition-all ${isMultilineMode ? 'text-indigo-400 bg-indigo-500/10' : 'text-gray-500 hover:text-gray-300'}`}
                    title={isMultilineMode ? "エディタモード (Enterで改行)" : "チャットモード (Enterで送信)"}
                    aria-label={isMultilineMode ? "エディタモード (Enterで改行)" : "チャットモード (Enterで送信)"}
                  >
                    {isMultilineMode ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
            <button aria-label="送信" type="submit" disabled={isSaving || isSinkRoomAtLimit || isInputOverLimit} className={`bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 text-white ${isLargeScreen ? 'p-2' : 'p-2.5'} rounded-full flex-shrink-0 shadow-lg transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed`}>
              <svg className={`${isLargeScreen ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
